import React, { useState, useEffect, createContext, useContext } from 'react';
import {
    Route,
    BrowserRouter as Router,
    Routes,
    Navigate,
    Outlet,
    useLocation,
    useParams
} from "react-router-dom";
import {
    useQuery,
    useQueryClient,
    QueryClient,
    QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { postGetTranslations } from '../../../../shared/apis/translations';
import MyProjects from './my-projects.jsx';
import ProjectDetail from './project-detail.jsx';
import ReviewQuote from './review-quote.jsx';
import ReceivedQuote from './received-quote.jsx';
import Search from './search.jsx';

//this flag is used to render the ReceivedQuote component on the /projects/index mvc view
let isReceivedQuoteView = false;
let modelProjectId = null;
const container = document.getElementById("received-quote-view");

if (container) {
    isReceivedQuoteView = true;
    modelProjectId = container.dataset.projectid;
}

export const CurrentProjectIdContext = createContext();
export const useCurrentProjectIdContext = () => useContext(CurrentProjectIdContext);

export const CurrentProjectIdProvider = ({ children }) => {
    const [currentProjectId, setCurrentProjectId] = useState(modelProjectId ? modelProjectId : null);
    
    return (
        <CurrentProjectIdContext.Provider value={{ currentProjectId, setCurrentProjectId }}>
            {children}
        </CurrentProjectIdContext.Provider>    
    )
};

export const translateProjectStatus = value => {
    let translationKey = null;

    switch (value) {
        case "In Progress":
            translationKey = "InProgress";
            break;
        case "Outgoing":
            translationKey = "Outgoing";
            break;
        case "Incoming":
            translationKey = "Incoming";
            break;
        case "Completed":
            translationKey = "Completed";
            break;
        case "Archived":
            translationKey = "Archived";
            break;
        case "None":
            translationKey = "None";
            break;
        default:
            translationKey = null;
            break;

    }

    return translationKey;
}

export const translateProjectType = value => {
    let translationKey = null;

    switch (value) {
        case "Independent Restaurant":
            translationKey = "IndependentRestaurant";
            break;
        case "Chain Restaurant":
            translationKey = "ChainRestaurant";
            break;
        case "Grocery":
            translationKey = "Grocery";
            break;
        case "C-Store":
            translationKey = "CStore";
            break;
        case "Stadium/Arena":
            translationKey = "StadiumArena";
            break;
        case "Zoo/Theater/Amusement Park":
            translationKey = "ZooTheaterAmusementPark";
            break;
        case "Golf Course":
            translationKey = "GolfCourse";
            break;
        case "Hotel/Casino":
            translationKey = "HotelCasino";
            break;
        case "Airport":
            translationKey = "Airport";
            break;
        case "Convention Center":
            translationKey = "ConventionCenter";
            break;
        case "Government/Military":
            translationKey = "GovernmentMilitary";
            break;
        case "Religious Organization":
            translationKey = "ReligiousOrganization";
            break;
        case "Business/Industry":
            translationKey = "BusinessIndustry";
            break;
        case "Ice Cream/Frozen Yogurt Shop":
            translationKey = "IceCreamFrozenYogurtShop";
            break;
        case "Distributor":
            translationKey = "Distributor";
            break;
        case "Other":
            translationKey = "Other";
            break;
        case "K-12":
            translationKey = "K12";
            break;
        case "College/University":
            translationKey = "CollegeUniversity";
            break;
        case "Hospital":
            translationKey = "Hospital";
            break;
        case "Assisted Living":
            translationKey = "AssistedLiving";
            break;
        default:
            translationKey = null;
            break;
    }

    return translationKey;

}

const queryClient = new QueryClient();

export const ScrollToTop = () => {
    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    return null;
}

export const TranslationContext = createContext();
export const useTranslationContext = () => useContext(TranslationContext);

export const TranslationProvider = ({ children, culture, conversionFactor }) => {
    const [translationLookup, setTranslationLookup] = useState(null);

    useEffect(() => {
        let translationKeys = {
            Accessories: 'ProjectBuilder.Label.Accessories',
            AddCustomGroup: 'ProjectBuilder.Button.AddCustomGroup',
            AddDiscount: 'ProjectBuilder.Label.AddDiscount',
            Accessory: 'ProjectBuilder.Label.Accessory',
            ListPrice: 'ProjectBuilder.Label.ListPrice',
            YourPrice: 'ProjectBuilder.Label.YourPrice',
            NetPrice: 'Product.Label.NetPrice',
            Price: 'Product.Label.Price',
            AddedAccessories: 'ProjectBuilder.Label.AddedAccessories',
            AddedAttachments: 'ProjectBuilder.Label.AddedAttachments',
            AddItem: 'ProjectBuilder.Label.AddItem',
            AddYourNotes: 'ProjectBuilder.Label.AddYourNotes',
            Apply: 'Global.Button.Apply',
            Archived: 'ProjectBuilder.Status.Archived',
            Attachment: 'ProjectBuilder.Label.Attachment',
            Attachments: 'ProjectBuilder.Label.Attachments',
            Documents: 'ProjectBuilder.Label.Documents',
            SearchDocuments: 'ProjectBuilder.Label.SearchDocuments',
            SearchVideos: 'ProjectBuilder.Label.SearchVideos',
            Videos: 'ProjectBuilder.Label.Videos',
            AttachmentRemoved: 'ProjectBuilder.Label.AttachmentRemoved',
            DeleteItem: 'ProjectBuilder.Label.DeleteItem',
            ArchiveModalConfirm: 'ProjectBuilder.Label.ArchiveModalConfirm',
            ArchiveModalNo: 'ProjectBuilder.Button.ArchiveModalNo',
            ArchiveModalYes: 'ProjectBuilder.Button.ArchiveModalYes',
            ArchiveThisProject: 'ProjectBuilder.Label.ArchiveThisProject',
            Attachment: 'ProjectBuilder.Label.Attachment',
            AttachmentAdded: 'ProjectBuilder.Label.AttachmentAdded',
            AttachmentRemoved: 'ProjectBuilder.Label.AttachmentRemoved',
            AttachmentsMoved: 'ProjectBuilder.Label.AttachmentsMoved',
            AttachmentsReturned: 'ProjectBuilder.Label.AttachmentsReturned',
            Cancel: 'Global.Button.Cancel',
            Completed: 'ProjectBuilder.Status.Completed',
            CreatedBy: 'Global.Label.CreatedBy',
            CreateProject: 'ProjectBuilder.Button.CreateProject',
            CustomDollarAmount: 'ProjectBuilder.Label.CustomDollarAmount',
            CustomDollarAmountDiscount: 'ProjectBuilder.Label.CustomDollarAmountDiscount',
            CustomPercentageDiscount: 'ProjectBuilder.Label.CustomPercentageDiscount',
            CustomPercentageDiscountLabel: 'ProjectBuilder.Label.CustomPercentageDiscountLabel',
            DeleteAttachmentAccept: 'ProjectBuilder.Button.DeleteAttachmentAccept',
            DeleteAttachmentCancel: 'ProjectBuilder.Button.DeleteAttachmentCancel',
            DeleteAttachmentConfirm: 'ProjectBuilder.Label.DeleteAttachmentConfirm',
            DeleteItemsAccept: 'ProjectBuilder.Button.DeleteItemsAccept',
            DeleteItemsCancel: 'ProjectBuilder.Button.DeleteItemsCancel',
            DeleteItemsConfirm: 'ProjectBuilder.Label.DeleteItemsConfirm',
            DeleteProductAccept: 'ProjectBuilder.Button.DeleteProductAccept',
            DeleteProductCancel: 'ProjectBuilder.Button.DeleteProductCancel',
            DeleteProductConfirm: 'ProjectBuilder.Label.DeleteProductConfirm',
            DeleteProjectAccept: 'ProjectBuilder.Button.DeleteProjectAccept',
            DeleteProjectCancel: 'ProjectBuilder.Button.DeleteProjectCancel',
            DeleteProjectConfirm: 'ProjectBuilder.Label.DeleteProjectConfirm',
            DeleteSectionAccept: 'ProjectBuilder.Button.DeleteSectionAccept',
            DeleteSectionCancel: 'ProjectBuilder.Button.DeleteSectionCancel',
            DeleteSectionConfirm: 'ProjectBuilder.Label.DeleteSectionConfirm',
            DeleteThisProject: 'ProjectBuilder.Label.DeleteThisProject',
            Documents: 'ProjectBuilder.Label.Documents',
            DollarAmount: 'ProjectBuilder.Label.DollarAmount',
            CustomDollarAmount: 'ProjectBuilder.Label.CustomDollarAmount',
            CustomPercentageDiscountLabel: 'ProjectBuilder.Label.CustomPercentageDiscountLabel',
            FiftyTenDiscount: 'ProjectBuilder.Label.FiftyTenDiscount',
            CustomPercentageDiscount: 'ProjectBuilder.Label.CustomPercentageDiscount',
            CustomDollarAmountDiscount: 'ProjectBuilder.Label.CustomDollarAmountDiscount',
            SelectDiscount: 'ProjectBuilder.Label.SelectDiscount',
            AddDiscount: 'ProjectBuilder.Label.AddDiscount',
            SelectDiscountExplanation: 'ProjectBuilder.Label.SelectDiscountExplanation',
            Discount: 'ProjectBuilder.Label.Discount',
            RemoveDiscount: 'ProjectBuilder.Label.RemoveDiscount',
            NetEach: 'ProjectBuilder.Label.NetEach',
            NetTotal: 'ProjectBuilder.Label.NetTotal',
            ListEach: 'ProjectBuilder.Label.ListEach',
            Qty: 'ProjectBuilder.Label.Qty',
            Apply: 'Global.Button.Apply',
            DownloadSellSheets: 'ProjectBuilder.Button.DownloadSellSheets',
            DownloadCsvQuote: 'ProjectBuilder.Button.DownloadCsvQuote',
            DownloadExcelQuote: 'ProjectBuilder.Button.DownloadExcelQuote',
            DownloadPdfQuote: 'ProjectBuilder.Button.DownloadPdfQuote',
            DownloadPDFQuoteProjectActions: 'ProjectBuilder.Button.DownloadPDFQuoteProjectActions',
            DuplicateProject: 'ProjectBuilder.Label.DuplicateProject',
            DuplicateThisProject: 'ProjectBuilder.Label.DuplicateThisProject',
            EditCustomGroup: 'ProjectBuilder.Button.EditCustomGroup',
            EditProject: 'ProjectBuilder.Label.EditProject',
            EditProjectDetails: 'ProjectBuilder.Label.EditProjectDetails',
            EditSection: 'ProjectBuilder.Label.EditSection',
            EnterYourComments: 'ProjectBuilder.Label.EnterYourComments',
            ErrorAccessories: 'Error.Accessories',
            ErrorAttachments: 'Error.Attachments',
            ErrorProjects: 'Error.Projects',
            FiftyTenDiscount: 'ProjectBuilder.Label.FiftyTenDiscount',
            FiftyTenOpenAmount: 'ProjectBuilder.Label.FiftyTenOpenAmount',
            FiftyTenOpenDiscount: 'ProjectBuilder.Label.FiftyTenOpenDiscount',
            GroupName: 'ProjectBuilder.Label.GroupName',
            Incoming: 'ProjectBuilder.Status.Incoming',
            InProgress: 'ProjectBuilder.Status.InProgress',
            LastUpdated: 'Global.Label.LastUpdated',
            MoveItemsAccept: 'ProjectBuilder.Button.MoveItemsAccept',
            MoveSelectedGroups: 'ProjectBuilder.Label.MoveSelectedGroups',
            MoveSelectedItems: 'ProjectBuilder.Label.MoveSelectedItems',
            MoveToGroup: 'ProjectBuilder.Label.MoveToGroup',
            MoveToProject: 'ProjectBuilder.Label.MoveToProject',
            MyProjects: 'Dashboard.Label.MyProjectsTxt',
            NA: 'Global.Label.NA',
            Name: 'Global.Label.Name',
            None: 'Global.Label.None',
            Notes: 'ProjectBuilder.Label.Notes',
            Outgoing: 'ProjectBuilder.Status.Outgoing',
            Overview: 'Global.Label.Overview',
            PercentageAmount: 'ProjectBuilder.Label.PercentageAmount',
            ProductsMoved: 'ProjectBuilder.Label.ProductsMoved',
            ProductsReturned: 'ProjectBuilder.Label.ProductsReturned',
            ProjectDeleted: 'ProjectBuilder.Label.ProjectDeleted',
            ProjectPDFDownloadName: 'ProjectBuilder.Label.ProjectPDFDownloadName',
            ProjectStatus: 'ProjectBuilder.Label.ProjectStatus',
            ProjectName: 'ProjectBuilder.Label.ProjectName',
            ProjectId: 'ProjectBuilder.Label.ProjectId',
            AccountName: 'ProjectBuilder.Label.AccountName',
            ZipCode: 'ProjectBuilder.Label.ZipCode',
            PhoneNumber: 'ProjectBuilder.Label.PhoneNumber',
            Customer: 'ProjectBuilder.Label.Customer',
            EmailAddress: 'ProjectBuilder.Label.EmailAddress',
            ProjectType: 'ProjectBuilder.Label.ProjectType',
            ProjectFormat: 'ProjectBuilder.Label.ProjectFormat',
            ProjectCost: 'ProjectBuilder.Label.ProjectCost',
            ProjectDiscount: 'ProjectBuilder.Label.ProjectDiscount',
            DiscountedCost: 'ProjectBuilder.Label.DiscountedCost',
            ProjectNotes: 'ProjectBuilder.Label.ProjectNotes',
            StatusInProgress: 'ProjectBuilder.Label.StatusInProgress',
            StatusOutgoing: 'ProjectBuilder.Label.StatusOutgoing',
            StatusIncoming: 'ProjectBuilder.Label.StatusIncoming',
            StatusCompleted: 'ProjectBuilder.Label.StatusCompleted',
            StatusArchived: 'ProjectBuilder.Label.StatusArchived',
            StatusNone: 'ProjectBuilder.Label.StatusNone',
            ProjectHeader: 'ProjectBuilder.Label.ProjectHeader',
            ProjectHeaderPlaceholder: 'ProjectBuilder.Label.ProjectHeaderPlaceholder',
            ProjectFormat: 'ProjectBuilder.Label.ProjectFormat',
            CloseOverview: 'ProjectBuilder.Label.CloseOverview',
            Close: 'Global.Buttons.Close',
            IndependentRestaurant: 'ProjectBuilder.Label.IndependentRestaurant',
            ChainRestaurant: 'ProjectBuilder.Label.ChainRestaurant',
            Grocery: 'ProjectBuilder.Label.Grocery',
            CStore: 'ProjectBuilder.Label.CStore',
            StadiumArena: 'ProjectBuilder.Label.StadiumArena',
            ZooTheaterAmusementPark: 'ProjectBuilder.Label.ZooTheaterAmusementPark',
            GolfCourse: 'ProjectBuilder.Label.GolfCourse',
            HotelCasino: 'ProjectBuilder.Label.HotelCasino',
            Airport: 'ProjectBuilder.Label.Airport',
            ConventionCenter: 'ProjectBuilder.Label.ConventionCenter',
            K12: 'ProjectBuilder.Label.K12',
            CollegeUniversity: 'ProjectBuilder.Label.CollegeUniversity',
            Hospital: 'ProjectBuilder.Label.Hospital',
            AssistedLiving: 'ProjectBuilder.Label.AssistedLiving',
            GovernmentMilitary: 'ProjectBuilder.Label.GovernmentMilitary',
            ReligiousOrganization: 'ProjectBuilder.Label.ReligiousOrganization',
            BusinessIndustry: 'ProjectBuilder.Label.BusinessIndustry',
            IceCreamFrozenYogurtShop: 'ProjectBuilder.Label.IceCreamFrozenYogurtShop',
            Distributor: 'ProjectBuilder.Label.Distributor',
            Other: 'ProjectBuilder.Label.Other',
            CreateNewProject: 'ProjectBuilder.Label.CreateNewProject',
            ReviewProject: 'ProjectBuilder.Label.ReviewProject',
            ReviewQuote: 'ProjectBuilder.Label.ReviewQuote',
            SendingTo: 'ProjectBuilder.Label.SendingTo',

            Subtotal: 'ProjectBuilder.Label.Subtotal',
            TaxFreightMessage: 'ProjectBuilder.Label.TaxFreightMessage',

            QuotePrice: 'ProjectBuilder.Label.QuotePrice',
            QuoteProject: 'ProjectBuilder.Label.QuoteProject',
            RecommendedAccessories: 'ProjectBuilder.Label.RecommendedAccessories',
            RecommendedAttachments: 'ProjectBuilder.Label.RecommendedAttachments',
            RemovedFromProject: 'ProjectBuilder.Label.RemovedFromProject',
            RemoveDiscount: 'ProjectBuilder.Label.RemoveDiscount',
            SalesProject: 'ProjectBuilder.Label.SalesProject',
            SaveChanges: 'Global.Button.SaveChanges',
            SaveGroup: 'ProjectBuilder.Button.SaveGroup',
            SearchDocuments: 'ProjectBuilder.Label.SearchDocuments',
            SearchProjects: 'ProjectBuilder.Label.SearchProjects',
            ArchiveThisProject: 'ProjectBuilder.Label.ArchiveThisProject',
            DeleteThisProject: 'ProjectBuilder.Label.DeleteThisProject',
            DownloadingPDF: 'ProjectBuilder.Label.DownloadingPDF',
            NewProject: 'ProjectBuilder.Label.NewProject',
            CreateProject: 'ProjectBuilder.Button.CreateProject',
            AddYourNotes: 'ProjectBuilder.Label.AddYourNotes',
            EnterYourComments: 'ProjectBuilder.Label.EnterYourComments',
            HandleQunatityErrorMsg: 'ProjectBuilder.Text.HandleQunatityErrorMsg',
            SelectDiscount: 'ProjectBuilder.Label.SelectDiscount',
            SelectDiscountExplanation: 'ProjectBuilder.Label.SelectDiscountExplanation',
            SelectGroup: 'ProjectBuilder.Label.SelectGroup',
            Submit: 'Global.Button.Submit',
            Undo: 'Global.Button.Undo',
            Videos: 'ProjectBuilder.Label.Videos',
            ShowProductNotesAll: 'ProjectBuilder.Label.ShowProductNotesAll',
            ShowProductNotesLess: 'ProjectBuilder.Label.ShowProductNotesLess',
            MoveSectionUp: 'ProjectBuilder.Label.MoveSectionUp',
            MoveSectionDown: 'ProjectBuilder.Label.MoveSectionDown',
            DeleteSection: 'ProjectBuilder.Label.DeleteSection',
            ProductsHidden: 'ProjectBuilder.Label.ProductsHidden',
            AddProduct: 'ProjectBuilder.Label.AddProduct',
            ErrorLoadingProduct: 'ProjectBuilder.Label.ErrorLoadingProduct',
            SelectAllProduct: 'ProjectBuilder.Label.SelectAllProduct',
            SortBy: 'ProjectBuilder.Label.SortBy',
            ProjectBuilderSearch: 'ProjectBuilder.Label.ProjectBuilderSearch',
            MoveProductsCustomGroups: 'ProjectBuilder.Label.MoveProductsCustomGroups',
            RecommendedDocumentAttachments: 'ProjectBuilder.Label.RecommendedDocumentAttachments',
            RecommendedDocumentAttachmentsTooltip: 'ProjectBuilder.Label.RecommendedDocumentAttachmentsTooltip',
            RecommendedVideoAttachments: 'ProjectBuilder.Label.RecommendedVideoAttachments',
            RecommendedVideoAttachmentsTooltip: 'ProjectBuilder.Label.RecommendedVideoAttachmentsTooltip',
            ShowAllRecommendedDocs: 'ProjectBuilder.Label.ShowAllRecommendedDocs',
            ShowLessRecommendedDocs: 'ProjectBuilder.Label.ShowLessRecommendedDocs',
            ShowAllRecommendedVideos: 'ProjectBuilder.Label.ShowAllRecommendedVideos',
            ShowLessRecommendedVideos: 'ProjectBuilder.Label.ShowLessRecommendedVideos',
            AddProjectDiscount: 'ProjectBuilder.Label.AddProjectDiscount',
            ProjectHeader: 'ProjectBuilder.Label.ProjectHeader',
            SelectProject: 'ProjectBuilder.Label.SelectProject',
            VideoAddedMsg: 'ProjectBuilder.Label.VideoAddedMsg',
            QuickAdd: 'ProjectBuilder.Label.QuickAdd',
            BtnAddToProjectBuilder: 'ProjectBuilder.Label.BtnAddToProjectBuilder',
            SeeDetails: 'ProjectBuilder.Label.SeeDetails',
            BtnAddToProject: 'ProjectBuilder.Label.BtnAddToProject',
            ProductNotes: 'ProjectBuilder.Label.ProductNotes',
            Information: 'ProjectBuilder.Label.Information',
            ContactEmailAddress: 'ProjectBuilder.Label.ContactEmailAddress',
            ContactPhoneNumber: 'ProjectBuilder.Label.ContactPhoneNumber',
            ProductTotal: 'ProjectBuilder.Label.ProductTotal',
            ThankYouMessage: 'ProjectBuilder.Label.ThankYouMessage',
            Date: 'ProjectBuilder.Label.Date',
            Acceptance: 'ProjectBuilder.Label.Acceptance',
            PrintedName: 'ProjectBuilder.Label.PrintedName',
            Products: 'Product.Label.Plural',
            ShowAll: 'Global.Label.ShowAll',
            ShowLess: 'Global.Label.ShowLess',
            BackTo: 'Global.Label.BackTo',
            Review: 'Global.Label.Review',
            Summary: 'Global.Label.Summary',
            Project: 'Global.Label.Project',
            Quote: 'Global.Label.Quote',
            SendingTo: 'ProjectBuilder.Label.SendingTo',
            StatusTxt: 'ProjectBuilder.Label.StatusTxt',
            Format: 'ProjectBuilder.Label.Format',
            SalesTax: 'ProjectBuilder.Label.SalesTax',
            Freight: 'ProjectBuilder.Label.Freight',
            ProjectNotesSaved: 'ProjectBuilder.Label.ProjectNotesSaved',
            ProjectHeaderSaved: 'ProjectBuilder.Label.ProjectHeaderSaved',
            ProjectNotesPlaceholder: 'ProjectBuilder.Label.ProjectNotesPlaceholder',
            SaveHeader: 'ProjectBuilder.Label.SaveHeader',
            Search: 'ProjectBuilder.Label.Search',
            NoResultsFound: 'Site.NoResultsFound',
            ViewAll: 'Global.Buttons.ViewAll',
            Categories: 'ProjectBuilder.Label.Categories',
            SearchForSkyOrKeywordsText: 'ProjectBuilder.Label.SearchForSkyOrKeywordsText',
            MsgBeforeSendQuote: 'ProjectBuilder.Label.MsgBeforeSendQuote',
            SendProject: 'ProjectBuilder.Label.SendProject',
            SendQuote: 'ProjectBuilder.Label.SendQuote',
            CopyLinkUrl: 'ProjectBuilder.Label.CopyLinkUrl',
            EmailMsg: 'ProjectBuilder.Label.EmailMsg',
            PriceDiscTxt: 'ProjectBuilder.Label.PriceDiscTxt',
            SKU: 'ProjectBuilder.Label.SKU',
            Product: 'Product.Label.Singular',
            Total: 'Global.Buttons.Total',
            SearchTitle: 'SearchLanding.Label.Title',
            SearchPlaceholder: 'SearchLanding.Label.Placeholder',
            DiscontinuedProducts: 'Search.Products.DiscontinuedProducts',
            LiquidatedProductsForSale: 'Search.Products.LiquidatedProductsForSale',
            ResultsFoundFor: 'SearchLanding.Label.ResultsFoundFor',
            ErrorGettingResults: 'Site.Errors.ErrorGettingResults',
            AdditionalFilters: 'Global.Label.AdditionalFilters',
            NoProjects: 'ProjectBuilder.Label.NoProjects',
            CollapseAll: 'Global.CollapseAll',
            ExpandAll: 'Global.ExpandAll',
            SortByAZ: 'ProjectBuilder.SortBy.AZ',
            SortByNewestAdded: 'ProjectBuilder.SortBy.NewestAdded',
            SortByProductFamily: 'ProjectBuilder.SortBy.ProductFamily',
            SortByCustomGrouping: 'ProjectBuilder.SortBy.CustomGrouping',
            SortBySortValue: 'ProjectBuilder.SortBy.SortValue'
        };

        postGetTranslations(Object.values(translationKeys))
            .then(data => {
                const lookup = {};
                Object.keys(translationKeys).forEach(key => {
                    lookup[key] = data[translationKeys[key]];
                });

                setTranslationLookup(lookup);
            });
    }, []);

    return (
        <TranslationContext.Provider value={{ translations: translationLookup, culture, conversionFactor }}>
            {!!translationLookup && children}
        </TranslationContext.Provider>
    );
};

function ProjectContainer() {
    const { currentProjectId, setCurrentProjectId } = useCurrentProjectIdContext();
    const { project } = useParams();

    useEffect(() => {
        // If page has been reloaded, the context value has to be set from the path
        if (project && !currentProjectId) {
            setCurrentProjectId(parseInt(project));
        }
    }, []);

    return !!currentProjectId && <Outlet />;
}

export default function ProjectBuilderMain({culture, conversionFactor}) {
    //const [sortBy, setSortBy] = useState(1);
    const [expandAll, setExpandAll] = useState(true);

    const [authToken, setAuthToken] = useState({
        ExpiresAtUtc: null,
        Token: null
    });

    const [isTokenError, setIsTokenError] = useState(false);

    let clientId = 'pujadas.es';
    let clientSecret = 'be8a3056-5180-4cb9-b2e6-bf3cf303a0e0';


    const getToken = async () => {
        let apiUrl = '/api/ClientToken/Get?clientId=';
        await fetch(apiUrl + clientId + '&clientSecret=' + clientSecret)
            .then(response => {
                if (!response.ok) {
                    setIsTokenError(true)
                    setAuthToken({
                        ExpiresAtUtc: null,
                        Token: null
                    })
                };
                return response.json();
            })
            .then(data => {
                setAuthToken({
                    ExpiresAtUtc: data.expiresAtUtc,
                    Token: data.token
                });
                sessionStorage.setItem('authToken', JSON.stringify(data));
            });
    }

    useEffect(() => {
        let sessionToken = JSON.parse(sessionStorage.getItem('authToken'));
        if (!(sessionToken === null)) {
            if (typeof sessionToken.Token !== 'undefined') {
                let tokenExpirationDate = new Date(sessionToken.ExpiresAtUtc);
                let now = new Date();
                if (tokenExpirationDate < now) {
                    sessionStorage.removeItem('authToken');
                    getToken();
                }
                else {
                    setAuthToken({
                        ExpiresAtUtc: sessionToken.ExpiresAtUtc,
                        Token: sessionToken.Token
                    });
                }
            }
            else {
                getToken();
            }
        }
        else {
            getToken();
        }
    }, [])

    return (
        <CurrentProjectIdProvider>
           <QueryClientProvider client={queryClient}>
                <TranslationProvider culture={culture} conversionFactor={conversionFactor}>
                    {authToken.Token ?
                        isReceivedQuoteView
                        ? <Router>
                            <Routes>
                                <Route path="*" element={<ReceivedQuote expandAll={expandAll} setExpandAll={setExpandAll} />} />
                            </Routes>
                            </Router>
                        : <>
                            <ScrollToTop />
                            <Routes>
                                <Route index element={<MyProjects />} />
                                <Route path=":project" element={<ProjectContainer />}>
                                    <Route index element={<ProjectDetail expandAll={expandAll} setExpandAll={setExpandAll} />} />
                                    <Route path="review-quote" element={<ReviewQuote expandAll={expandAll} setExpandAll={setExpandAll} />} />
                                    <Route path="received-quote" element={<ReceivedQuote expandAll={expandAll} setExpandAll={setExpandAll} />} />
                                    <Route path="search/*" element={<Search />} />
                                </Route>
                            </Routes>
                        </>
                    : <div className="container text-center">Getting Authorization Token...</div>
                    }
                    <ReactQueryDevtools />
                </TranslationProvider>
            </QueryClientProvider>
        </CurrentProjectIdProvider>
    );
}
